import React, { useState } from 'react';
import { Paper, Box, Divider, PaperProps, TextField, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { extractErrorMessage } from '../../../utils/errorUtils';
import { OptionAlreadyExistsInfoIcon } from '../../dialogs/ManageLeadColumns/OptionAlreadyExistsInfoIcon';

export interface OptionMenuPaperProps extends PaperProps {
  onAddOption?: (newOption: string) => Promise<void> | void;
  onActionClick?: () => void;
  actionText?: string;
}

export const OptionMenuPaper = React.forwardRef<HTMLDivElement, OptionMenuPaperProps>(
  ({ children, onAddOption, onActionClick, actionText = 'Add Option', ...rest }, ref) => {
    const [isAdding, setIsAdding] = useState(false);
    const [inputVal, setInputVal] = useState('');
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);

    const listContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      const timer = setTimeout(() => {
        if (listContainerRef.current) {
          const selectedEl = listContainerRef.current.querySelector<HTMLElement>('.Mui-selected, [aria-selected="true"]');
          if (selectedEl) {
            const container = listContainerRef.current;
            const containerRect = container.getBoundingClientRect();
            const selectedRect = selectedEl.getBoundingClientRect();
            const relativeTop = selectedRect.top - containerRect.top + container.scrollTop;
            const targetScrollTop = relativeTop - container.clientHeight / 2 + selectedRect.height / 2;
            container.scrollTop = Math.max(0, targetScrollTop);
          } else {
            listContainerRef.current.scrollTop = 0;
          }
        }
      }, 0);
      return () => clearTimeout(timer);
    }, []);

    const handleAdd = async () => {
      setErrorMsg(null);
      const trimmed = inputVal.trim();
      if (!trimmed || !onAddOption) return;
      if (trimmed.length > 25) {
        setErrorMsg('Option name cannot exceed 25 characters.');
        return;
      }
      try {
        setLoading(true);
        await onAddOption(trimmed);
        setInputVal('');
        setIsAdding(false);
        setTimeout(() => {
          if (listContainerRef.current) {
            listContainerRef.current.scrollTo({
              top: listContainerRef.current.scrollHeight,
              behavior: 'smooth',
            });
          }
        }, 50);
      } catch (err: any) {
        console.error('Failed to add option:', err);
        setErrorMsg(extractErrorMessage(err, `Option "${trimmed}" already exists.`));
      } finally {
        setLoading(false);
      }
    };

    const hasBottomAction = Boolean(onAddOption || onActionClick);
    const displayError = errorMsg || (inputVal.length >= 25 ? 'Option name cannot exceed 25 characters.' : null);

    const parseWidth = (w: string | number | undefined) => {
      if (typeof w === 'number') return w;
      if (typeof w === 'string') return parseInt(w, 10) || 0;
      return 0;
    };
    const incomingWidth = parseWidth(rest.style?.minWidth || rest.style?.width);
    const paperStyle: React.CSSProperties = {
      ...rest.style,
      minWidth: incomingWidth > 0 ? incomingWidth : '100%',
      width: 'max-content',
      maxWidth: 'calc(100vw - 32px)',
    };

    return (
      <Paper
        ref={ref}
        {...rest}
        style={paperStyle}
        sx={{
          minWidth: incomingWidth > 0 ? incomingWidth : '100%',
          width: 'max-content',
          maxWidth: 'calc(100vw - 32px)',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: 280,
          overflow: 'hidden',
          ...rest.sx,
        }}
      >
        <Box ref={listContainerRef} sx={{ overflowY: 'auto', overflowX: 'hidden', flex: 1, maxHeight: isAdding ? 140 : 200, width: '100%', boxSizing: 'border-box' }}>
          {children}
        </Box>
        {hasBottomAction && (
          <>
            <Divider sx={{ my: 0 }} />
            <Box
              sx={{
                bgcolor: 'background.paper',
                minHeight: 40,
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onMouseUp={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
            >
              {isAdding ? (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    width: '100%',
                    py: 0.75,
                    px: 1.5,
                    boxSizing: 'border-box',
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}>
                    <TextField
                      error={!!displayError}
                      size="small"
                      autoFocus
                      placeholder="New option name..."
                      value={inputVal}
                      onChange={(e) => {
                        setInputVal(e.target.value);
                        if (errorMsg) setErrorMsg(null);
                      }}
                      inputProps={{ maxLength: 25 }}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAdd();
                        } else if (e.key === 'Escape') {
                          e.preventDefault();
                          setIsAdding(false);
                          setInputVal('');
                          setErrorMsg(null);
                        }
                      }}
                      onKeyUp={(e) => e.stopPropagation()}
                      onKeyPress={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        '& .MuiOutlinedInput-root': {
                          height: 30,
                          fontSize: '0.8125rem',
                        },
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleAdd}
                      disabled={!inputVal.trim() || loading}
                      sx={{
                        color: 'primary.main',
                        p: 0.5,
                        '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.08)' },
                        '&.Mui-disabled': { color: 'action.disabled' },
                      }}
                      title="Save option"
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setIsAdding(false);
                        setInputVal('');
                        setErrorMsg(null);
                      }}
                      sx={{
                        color: 'text.secondary',
                        p: 0.5,
                        '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
                      }}
                      title="Cancel"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {displayError && (
                    <Box sx={{ display: 'flex', alignItems: 'center', px: 0.5, pt: 0.25 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: displayError.includes('already exists') ? 'error.main' : 'warning.main',
                          fontSize: '0.75rem',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          lineHeight: 1.25,
                        }}
                      >
                        {displayError}
                      </Typography>
                      {displayError.toLowerCase().includes('already exists') && <OptionAlreadyExistsInfoIcon />}
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (onActionClick) {
                      onActionClick();
                    } else {
                      setIsAdding(true);
                    }
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: '100%',
                    minHeight: 40,
                    py: 0.75,
                    px: 1.5,
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <AddIcon fontSize="small" />
                  <Typography variant="body2" fontWeight={600} color="primary.main">
                    {actionText}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}
      </Paper>
    );
  }
);

OptionMenuPaper.displayName = 'OptionMenuPaper';

export default OptionMenuPaper;
